import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentPreview,
  MiButton,
  MiIcon,
  MiIconButton,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
  PERMISSIONS,
} from "@miplatform/constants";
import { formatDate, getCurrencyString, getFullName } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import {
  BillingCommitmentLabelMap,
  INVOICING_ROUTES,
} from "../../../accountingConstants";
import { formatOpenBalanceDisplay } from "../../reconcileConstants";

export const PurchaseOrderDetails = memo(
  ({
    purchaseOrder,
    isReviewMode,
    viewMode,
    onBack,
    onEdit,
    onDelete,
    onVoid,
  }) => {
    const { hasPermission } = usePermissions();
    const { voidDate } = purchaseOrder || {};

    const { classes } = useStyles({ voidDate });
    return (
      <>
        <Box display="flex" justifyContent="space-between" p={2} pt={1} mb={2}>
          <Box display="flex" gap={4} flexWrap="wrap">
            <MiButton
              variant="text"
              size="small"
              color={BUTTON_COLORS.GREY}
              startIcon={{ path: mdi[ICONS_WEB.ARROW_LEFT] }}
              onClick={onBack}
            />
            <MiStackedTypography
              title="Date Added"
              description={formatDate(
                purchaseOrder?.modifiedOn,
                DATE_TIME_FORMATS.FORMAT_MM_DD_YY
              )}
              descriptionProps={{
                className: classes.fieldDescription,
              }}
            />
            <MiStackedTypography
              title="Added By"
              description={getFullName(
                purchaseOrder?.orderCreatedByUser?.firstName,
                purchaseOrder?.orderCreatedByUser?.lastName
              )}
              descriptionProps={{
                className: classes.fieldDescription,
              }}
            />
            <MiStackedTypography
              title="Payment Information #"
              description={purchaseOrder?.number}
              descriptionProps={{
                className: classes.fieldDescription,
              }}
            />
            <MiStackedTypography
              title="Payment Method"
              description={
                BillingCommitmentLabelMap[purchaseOrder?.type] || "Unknown"
              }
              descriptionProps={{
                className: classes.fieldDescription,
              }}
            />
            <MiStackedTypography
              title="Amount"
              description={getCurrencyString(purchaseOrder?.amount)}
              descriptionProps={{
                className: classes.fieldDescription,
              }}
            />
            <MiStackedTypography
              title="Open Balance"
              description={
                purchaseOrder?.openBalance < 0
                  ? formatOpenBalanceDisplay(purchaseOrder?.openBalance)
                  : formatOpenBalanceDisplay(0)
              }
              descriptionProps={{
                className: classes.fieldDescription,
                color: purchaseOrder?.openBalance < 0 ? "red.main" : "inherit",
              }}
            />
            <MiStackedTypography
              title="Excess Balance"
              description={
                purchaseOrder?.openBalance > 0
                  ? formatOpenBalanceDisplay(purchaseOrder?.openBalance)
                  : formatOpenBalanceDisplay(0)
              }
              descriptionProps={{
                className: classes.fieldDescription,
                color:
                  purchaseOrder?.openBalance > 0 ? "green.main" : "inherit",
              }}
            />
          </Box>
          {!isReviewMode && !voidDate && (
            <Box display="flex" alignItems="center">
              {hasPermission(PERMISSIONS.BILLING_COMMITMENT.UPDATE) &&
                viewMode !== INVOICING_ROUTES.INVOICED && (
                  <MiIconButton onClick={() => onEdit(purchaseOrder)}>
                    <MiIcon path={mdi[ICONS_WEB.PENCIL_OUTLINE]} />
                  </MiIconButton>
                )}
              {hasPermission(PERMISSIONS.BILLING_COMMITMENT.VOID) && (
                <MiIconButton onClick={() => onVoid(purchaseOrder)}>
                  <MiIcon path={mdi[ICONS_WEB.CANCEL]} />
                </MiIconButton>
              )}
              {hasPermission(PERMISSIONS.BILLING_COMMITMENT.DELETE) &&
                viewMode !== INVOICING_ROUTES.INVOICED && (
                  <MiIconButton onClick={() => onDelete(purchaseOrder)}>
                    <MiIcon path={mdi[ICONS_WEB.TRASH_CAN]} />
                  </MiIconButton>
                )}
            </Box>
          )}
        </Box>

        <DocumentPreview selectedDocument={purchaseOrder?.document} />
      </>
    );
  }
);

const useStyles = makeStyles()((theme, { voidDate } = {}) => ({
  fieldDescription: {
    maxWidth: 110,
    fontWeight: theme.fontWeight.bold,
    ...(voidDate && {
      textDecoration: "line-through",
      color: theme.palette.error.main,
    }),
  },
}));
